import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { SpecialDayInfo } from '@/types/api';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import { getSyncStatusBadge } from '@/utils/syncStatus';

interface SpecialDaysTabProps {
  restaurantId: number;
}

export function SpecialDaysTab({ restaurantId }: SpecialDaysTabProps) {
  const queryClient = useQueryClient();

  const canCreate = useHasPermission(PERM.BOOKING_CREATE);
  const canUpdate = useHasPermission(PERM.BOOKING_UPDATE);
  const canDelete = useHasPermission(PERM.BOOKING_DELETE);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSpecialDay, setEditingSpecialDay] = useState<SpecialDayInfo | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch special days
  const { data: specialDays = [], isLoading } = useQuery({
    queryKey: ['special-days', restaurantId],
    queryFn: () => apiClient.getSpecialDays(restaurantId),
    enabled: !!restaurantId,
  });

  // Delete mutation — uuid is the cross-table identifier.
  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => apiClient.deleteSpecialDay(restaurantId, uuid),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['special-days', restaurantId] });
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.message || 'Failed to delete special day');
    },
  });

  const handleDelete = async (uuid: string | undefined) => {
    if (!uuid) return;
    if (window.confirm('Are you sure you want to delete this special day?')) {
      try {
        await deleteMutation.mutateAsync(uuid);
      } catch {
        // surfaced via deleteMutation's onError -> deleteError banner
      }
    }
  };

  const handleEdit = (specialDay: SpecialDayInfo) => {
    setEditingSpecialDay(specialDay);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingSpecialDay(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingSpecialDay(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['special-days', restaurantId] });
    setIsModalOpen(false);
    setEditingSpecialDay(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading special days...</div>
      </div>
    );
  }

  return (
    <>
      {deleteError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center justify-between">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="text-red-700 hover:text-red-900 font-medium ml-4">
            Dismiss
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {specialDays.length} special {specialDays.length === 1 ? 'day' : 'days'}
        </div>
        {canCreate && (
          <button onClick={handleCreate} className="btn btn-primary h-10">
            <Plus className="h-4 w-4 mr-2" />
            Add Special Day
          </button>
        )}
      </div>

      {/* Table */}
      {specialDays.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500">No special days configured yet.</p>
          <p className="text-sm text-gray-400 mt-1">
            Add special days to override normal booking schedules (e.g., holidays, events).
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Closed?
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Open Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Close Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Max Bookings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sync Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {specialDays.map((day) => {
                  const badge = getSyncStatusBadge(day.syncStatus);
                  return (
                    <tr key={day.uuid ?? day.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {day.date}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate">
                        {day.description || '\u2014'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            day.isClosed
                              ? 'bg-red-100 text-red-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {day.isClosed ? 'Closed' : 'Open'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {day.openTime || '\u2014'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {day.closeTime || '\u2014'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {day.maxBookings ?? '\u2014'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-1">
                          {canUpdate && (
                            <button
                              onClick={() => handleEdit(day)}
                              className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                              aria-label={`Edit special day ${day.date}`}
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(day.uuid as unknown as string)}
                              className="text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                              aria-label={`Delete special day ${day.date}`}
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <SpecialDayModal
          restaurantId={restaurantId}
          specialDay={editingSpecialDay}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
}

// Special Day Modal Component for Create/Edit
interface SpecialDayModalProps {
  restaurantId: number;
  specialDay: SpecialDayInfo | null;
  onClose: () => void;
  onSuccess: () => void;
}

function SpecialDayModal({ restaurantId, specialDay, onClose, onSuccess }: SpecialDayModalProps) {
  const [formData, setFormData] = useState({
    date: specialDay?.date || '',
    description: specialDay?.description || '',
    isClosed: specialDay?.isClosed ?? false,
    openTime: specialDay?.openTime || '',
    closeTime: specialDay?.closeTime || '',
    maxBookings: specialDay?.maxBookings ?? ('' as number | ''),
    notes: specialDay?.notes || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: (data: Partial<SpecialDayInfo>) =>
      apiClient.createSpecialDay(restaurantId, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create special day';
      setErrors({ submit: message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<SpecialDayInfo>) =>
      apiClient.updateSpecialDay(restaurantId, specialDay!.uuid as unknown as string, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update special day';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData: Partial<SpecialDayInfo> = {
      date: formData.date,
      description: formData.description || undefined,
      isClosed: formData.isClosed,
      openTime: formData.isClosed ? undefined : formData.openTime || undefined,
      closeTime: formData.isClosed ? undefined : formData.closeTime || undefined,
      maxBookings: formData.maxBookings !== '' ? Number(formData.maxBookings) : undefined,
      notes: formData.notes || undefined,
    };

    if (specialDay) {
      await updateMutation.mutateAsync(submitData);
    } else {
      await createMutation.mutateAsync(submitData);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {specialDay ? 'Edit Special Day' : 'Add Special Day'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="special-day-date" className="block text-sm font-medium text-gray-700 mb-1">
              Date *
            </label>
            <input
              id="special-day-date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className={`input w-full ${errors.date ? 'border-red-500' : ''}`}
            />
            {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date}</p>}
          </div>

          <div>
            <label htmlFor="special-day-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              id="special-day-description"
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              placeholder="e.g., Christmas Day, New Year's Eve"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="special-day-closed"
              checked={formData.isClosed}
              onChange={(e) => setFormData({ ...formData, isClosed: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="special-day-closed" className="ml-2 text-sm text-gray-700">
              Closed (no bookings accepted)
            </label>
          </div>

          <div>
            <label htmlFor="special-day-open-time" className="block text-sm font-medium text-gray-700 mb-1">
              Open Time
            </label>
            <input
              id="special-day-open-time"
              type="time"
              value={formData.openTime}
              onChange={(e) => setFormData({ ...formData, openTime: e.target.value })}
              className="input w-full"
              disabled={formData.isClosed}
            />
          </div>

          <div>
            <label htmlFor="special-day-close-time" className="block text-sm font-medium text-gray-700 mb-1">
              Close Time
            </label>
            <input
              id="special-day-close-time"
              type="time"
              value={formData.closeTime}
              onChange={(e) => setFormData({ ...formData, closeTime: e.target.value })}
              className="input w-full"
              disabled={formData.isClosed}
            />
          </div>

          <div>
            <label htmlFor="special-day-max-bookings" className="block text-sm font-medium text-gray-700 mb-1">
              Max Bookings
            </label>
            <input
              id="special-day-max-bookings"
              type="number"
              min="0"
              value={formData.maxBookings}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxBookings: e.target.value ? parseInt(e.target.value) : '',
                })
              }
              className="input w-full"
              placeholder="Leave blank for unlimited"
            />
          </div>

          <div>
            <label htmlFor="special-day-notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              id="special-day-notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input w-full"
              rows={3}
              placeholder="Additional notes about this special day"
            />
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : specialDay
                ? 'Update Special Day'
                : 'Create Special Day'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SpecialDaysTab;